/**
 * Phrases that describe an internal state rather than an observable action.
 *
 * The guidance panel lists `phrase`; the linter flags `pattern`. One entry
 * each, so the panel shows exactly what the linter enforces.
 *
 * @module taxonomy/vague-verbs
 */

/** One vague phrase and how the linter recognises it. */
export interface VagueVerb {
  id: string;
  phrase: string;
  pattern: RegExp;
  message: string;
  suggestions: readonly string[];
}

export const VAGUE_VERBS: readonly VagueVerb[] = [
  {
    id: "vague_understand",
    phrase: "understand",
    pattern: /\b(understand|understands|understanding)\b/gi,
    message: "'Understand' is hard to assess directly. Use an observable verb instead.",
    suggestions: ["describe", "explain", "apply", "analyse", "evaluate"],
  },
  {
    // Word-bounded so "knowledge" is left to vague_knowledge; "know-how" is a noun.
    id: "vague_know",
    phrase: "know",
    pattern: /\b(know|knows)\b(?!-)/gi,
    message: "'Know' is not observable. State how learners will show what they know.",
    suggestions: ["identify", "recall", "describe", "explain"],
  },
  {
    // Not "learning", which is as often a noun ("machine learning").
    id: "vague_learn",
    phrase: "learn",
    pattern: /\b(learn|learns)\b/gi,
    message:
      "'Learn' describes the process, not the result. State what learners will be able to do.",
    suggestions: ["demonstrate", "apply", "explain", "use"],
  },
  {
    id: "vague_aware",
    phrase: "be aware of",
    pattern: /\b(aware of|awareness of)\b/gi,
    message: "'Aware of' is often too soft. Specify the behaviour or output.",
    suggestions: ["recognise", "identify", "explain", "evaluate"],
  },
  {
    id: "vague_familiar",
    phrase: "be familiar with",
    pattern: /\b(be familiar with|become familiar with|familiar with)\b/gi,
    message: "'Familiar with' is usually not measurable. State what learners will *do*.",
    suggestions: ["use", "select", "demonstrate", "interpret"],
  },
  {
    id: "vague_appreciate",
    phrase: "appreciate",
    pattern: /\b(appreciate|appreciates|appreciation of)\b/gi,
    message: "'Appreciate' names an attitude, not an action. Say how learners will show it.",
    suggestions: ["explain", "discuss", "justify", "evaluate"],
  },
  {
    id: "vague_knowledge",
    phrase: "have knowledge of",
    pattern: /\b(have knowledge of|has knowledge of|knowledge of|be knowledgeable about)\b/gi,
    message: "Vague knowledge phrasing. Prefer a demonstrable action.",
    suggestions: ["identify", "summarise", "compare", "apply", "justify"],
  },
  {
    id: "vague_insight",
    phrase: "gain an insight into",
    pattern: /\b(gain|gains|gaining|gained) (an |some )?insights? into\b/gi,
    message: "'Gain an insight into' does not say what learners will do with it.",
    suggestions: ["analyse", "examine", "interpret", "explain"],
  },
];
